import { useMutation, useQueryClient } from '@tanstack/react-query';
import { instance } from '~/utils/api/api';
import {
  IsSort,
  Table,
  TableBody,
  TableCell,
  TableCellHead,
  TableHead,
  TableRow,
} from '~/ui/Table';
import { IconButton } from '~/ui';
import Basket from '~/assets/svg/newSvg/basket.svg?react';
import { useNavigate } from 'react-router-dom';
import styles from './NotificationsTable.module.scss';
import { renderStatus } from '../../utils';
import ModalConfirm from '~/components/Shared/ModalConfirm';
import TooltipText from '~/components/Shared/TooltipText/TooltipText';
import { useTranslation } from 'react-i18next';
import CustomCheckbox from '~/components/Shared/CustomCheckbox/CustomCheckbox';
import dayjs from 'dayjs';
import { useToastSuccess } from '~/utils/useToastSuccess';
import { useToastError } from '~/utils/useToastError';
import { getPaginate } from '~/utils/getPaginate';

export interface Notification {
  id: number;
  module_name: string;
  category_name: string;
  group_send: string;
  condition: string;
  comment: string;
  status_id: number;
  date: string;
}

interface NotificationsTableProps {
  notifications: Notification[];
  sort: IsSort;
  setSort: ({ field, direction }: IsSort) => void;
  selectedItems: number[];
  handleToggleAll: () => void;
  handleToggle: (id: number) => void;
  isLoading?: boolean;
}

export const NotificationsTable = ({
  notifications,
  sort,
  setSort,
  selectedItems,
  handleToggleAll,
  handleToggle,
  isLoading,
}: NotificationsTableProps) => {
  const client = useQueryClient();
  const toastSuccess = useToastSuccess();
  const toastError = useToastError();
  const { t } = useTranslation();
  const { value, paginate } = getPaginate();
  const navigate = useNavigate();

  const { mutate: deleteNotification } = useMutation(
    async (id: string) => {
      const response = await instance.delete(`admin/notification/${id}`);
      return response.data;
    },
    {
      onSuccess: () => {
        client.invalidateQueries(['notifications']);
        toastSuccess(t('notification_deleted'));
      },
      onError: error => {
        toastError(error);
      },
    }
  );

  return (
    <div className={styles.tableWrapper}>
      <Table>
        <TableHead sort={sort} setSort={setSort}>
          <TableCellHead
            className={styles.tableCellHead}
            sortable={false}
            field="checkbox"
          >
            <CustomCheckbox
              isChecked={
                selectedItems.length === notifications.length &&
                !!notifications.length
              }
              onChange={handleToggleAll}
            />
          </TableCellHead>
          <TableCellHead field="module">{t('module')}</TableCellHead>
          <TableCellHead field="comment">{t('comment')}</TableCellHead>
          <TableCellHead field="status_id">{t('status')}</TableCellHead>
          <TableCellHead field="date">{t('send_date')}</TableCellHead>
          <TableCellHead sortable={false} field="actions">
            {t('action')}
          </TableCellHead>
        </TableHead>
        <TableBody isLoading={isLoading} loadingRows={5}>
          {notifications.map(
            ({ id, module_name, comment, status_id, date }: Notification) => (
              <TableRow key={id} onClick={() => navigate(`/notifications/${id}?${value}=${paginate}`)}>
                <TableCell>
                  <CustomCheckbox
                    isChecked={selectedItems.includes(id)}
                    onChange={() => handleToggle(id)}
                  />
                </TableCell>
                <TableCell>{module_name}</TableCell>
                <TableCell className={styles.commentCell}>
                  <TooltipText text={comment} />
                </TableCell>
                <TableCell>{renderStatus(status_id)}</TableCell>
                <TableCell>{dayjs(date).format('DD.MM.YYYY')}</TableCell>
                <TableCell>
                  <div className={styles.notificationsTableActions}>
                    <ModalConfirm
                      title={`${t('delete_notification_confirm')} «${comment}»`}
                      onConfirm={() => deleteNotification(id.toString())}
                    >
                      {open => (
                        <span onClick={e => e.stopPropagation()}>
                          <IconButton onClick={open}>
                            <Basket />
                          </IconButton>
                        </span>
                      )}
                    </ModalConfirm>
                  </div>
                </TableCell>
              </TableRow>
            )
          )}
        </TableBody>
      </Table>
    </div>
  );
};
